import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, Outlet, useParams } from '@tanstack/react-router';
import { pickActive, setActiveHive, useActiveHiveId } from '../lib/activeHive.js';
import { PushToggle } from './PushToggle.js';
import { TickerBar } from './TickerBar.js';
import { useTRPC } from '../lib/trpc.js';

const TABS = [
  { to: '/w/$worldId', label: 'Karte', icon: '🗺', exact: true },
  { to: '/w/$worldId/stock', label: 'Stock', icon: '⬢' },
  { to: '/w/$worldId/zuege', label: 'Züge', icon: '➶' },
  { to: '/w/$worldId/chat', label: 'Chat', icon: '💬' },
  { to: '/w/$worldId/berichte', label: 'Berichte', icon: '📜' },
] as const;

/** Rahmen einer Welt: unter 640 px Bottom-Tabs, darüber Tab-Leiste oben (Konzept, Abschnitt 10). */
export function WorldShell() {
  const { worldId } = useParams({ from: '/w/$worldId' });
  const trpc = useTRPC();
  const me = useQuery(trpc.auth.me.queryOptions());
  const unread = useQuery({
    ...trpc.report.unreadCount.queryOptions({ worldId: Number(worldId) }),
    enabled: !!me.data?.account,
    refetchInterval: 60_000,
  });
  const chatUnread = useQuery({
    ...trpc.chat.unread.queryOptions({ worldId: Number(worldId) }),
    enabled: !!me.data?.account,
    refetchInterval: 60_000,
  });
  const mine = useQuery({
    ...trpc.hive.mine.queryOptions({ worldId: Number(worldId) }),
    enabled: !!me.data?.account,
  });
  const activeId = useActiveHiveId(Number(worldId));
  const active = pickActive(mine.data?.hives, activeId);
  const badges: Record<string, number> = {
    Berichte: unread.data?.count ?? 0,
    Chat: chatUnread.data?.count ?? 0,
  };

  if (me.isPending) return <p className="p-6 text-stone-400">Lade…</p>;
  if (!me.data?.account) return <Navigate to="/login" />;
  const player = me.data.players.find((p) => String(p.worldId) === worldId);
  if (!player) return <Navigate to="/" />;

  return (
    <div className="flex flex-1 flex-col gap-4 pb-20 sm:pb-0">
      <nav className="hidden gap-1 rounded-xl bg-stone-950/40 p-1 sm:flex">
        {TABS.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            params={{ worldId }}
            activeOptions={{ exact: 'exact' in t }}
            className="flex-1 rounded-lg px-3 py-2 text-center text-sm text-stone-300 hover:bg-stone-800"
            activeProps={{ className: 'bg-stone-800 text-amber-300' }}
          >
            {t.label}
            {(badges[t.label] ?? 0) > 0 && (
              <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-stone-950">
                {badges[t.label]}
              </span>
            )}
          </Link>
        ))}
      </nav>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <TickerBar worldId={Number(worldId)} />
        <PushToggle />
      </div>
      {(mine.data?.hives.length ?? 0) > 1 && active && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-stone-400">Aktiver Stock</span>
          <select
            className="rounded-md border border-stone-700 bg-stone-900 px-2 py-1 text-sm"
            value={active.id}
            onChange={(e) => setActiveHive(Number(worldId), Number(e.target.value))}
          >
            {mine.data!.hives.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name} [{h.q}|{h.r}]
              </option>
            ))}
          </select>
        </div>
      )}
      <Outlet />
      <nav className="fixed inset-x-0 bottom-0 z-10 grid grid-cols-5 border-t border-stone-800 bg-stone-950/95 pb-[env(safe-area-inset-bottom)] backdrop-blur sm:hidden">
        {TABS.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            params={{ worldId }}
            activeOptions={{ exact: 'exact' in t }}
            className="flex min-h-14 flex-col items-center justify-center gap-0.5 text-[11px] text-stone-400"
            activeProps={{ className: 'text-amber-300' }}
          >
            <span className="text-lg leading-none">{t.icon}</span>
            {t.label}
            {(badges[t.label] ?? 0) > 0 && (
              <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-stone-950">
                {badges[t.label]}
              </span>
            )}
          </Link>
        ))}
      </nav>
    </div>
  );
}
